import React, {useState, useCallback, useContext, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {IconoirPlusCircle} from '@ey-xd/motif-icon';
import {useEngagementId} from '../../../../../../util/customHooks';
import {DocumentEngagementIdContext} from '../../../../../Main/Forms/CanvasFormContainer';
import {clientSideEventNames, entities, Entity} from '../../../../../../util/uiconstants';
import ClickableIconLabel from '../../../../ClickableIconLabel/ClickableIconLabel';
import CreateEditEntityModal from '../../../../../Common/EntityCrud/CreateEditEntityModal';
import {CreateUpdateEntityContainer} from './CreateUpdateEntities/index';

export default function BodySpecialToolbarButton({
	onClick,
	className,
	label,
	disabled,
	entityId,
	bodyId,
	bodyTypeId,
	...otherProps
}) {
	const modalRef = useRef();

	const engagementId = useEngagementId();
	const documentEngagementId = useContext(DocumentEngagementIdContext);

	const [showModal, setShowModal] = useState(false);

	// Get Entity Type based on entityId
	const entityType = useMemo(() => {
		switch (entityId) {
			case entities.ItRisk:
				return Entity.ITRisk;
			case entities.ItControl:
				return Entity.ITControl;
			case entities.ItSoApplication:
				return Entity.ITSOApplication;
			default:
				break;
		}
	}, [entityId]);

	const handleOnBtnClick = useCallback(
		(e) => {
			if (entityType === entities.ItSoApplication) {
				toggleModal(e);
			}
			setShowModal(!showModal);
		},
		[onClick, modalRef]
	);

	const toggleModal = (e) => {
		onClick && onClick(e);
		if (modalRef?.current?.toggleModal) {
			modalRef.current.toggleModal();
		}
	};

	const handleOnEntityCreated = () => {
		if (entityType === Entity.ITRisk) {
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		}
	}

	return (
		<StyledBodySpecialToolbarButton className={`StyledBodySpecialToolbarButton ${className}`}>
			<ClickableIconLabel disabled={disabled} text={label} iconName={IconoirPlusCircle} onClick={handleOnBtnClick} />
			{!disabled && entityType !== Entity.ITSOApplication && (
				<CreateEditEntityModal
					engagementId={engagementId}
					entityType={entityType}
					showCreateEditEntityModal={showModal}
					setShowCreateEditEntityModal={setShowModal}
					isITGC={false}
					onEntityCreated={handleOnEntityCreated}
				/>
			)}
			{!disabled && entityType === Entity.ITSOApplication && (
				<CreateUpdateEntityContainer
					{...otherProps}
					entityId={entityId}
					engagementId={engagementId}
					documentEngagementId={documentEngagementId}
					bodyId={bodyId}
					bodyTypeId={bodyTypeId}
					ref={modalRef}
				/>
			)}
		</StyledBodySpecialToolbarButton>
	);
}

BodySpecialToolbarButton.propTypes = {
	className: PropTypes.string,
	label: PropTypes.string.isRequired,
	children: PropTypes.node,
	disabled: PropTypes.bool,
	onClick: PropTypes.func,
	entityId: PropTypes.number,
	bodyId: PropTypes.number.isRequired
};

BodySpecialToolbarButton.defaultProps = {
	className: 'body-special-toolbar-button',
	disabled: false
};

const StyledBodySpecialToolbarButton = styled.section`
	display: flex;
	align-items: flex-end;
	margin-left: auto;
`;
